import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { MagnifyingGlass, KeyReturn, Copy, Warning, UserPlus, ArrowClockwise } from "@phosphor-icons/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

function generatePassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null); // client selected for password reset

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (e) {
      console.error("[AdminClients] fetchClients failed:", e);
      toast.error("Failed to load clients");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = clients.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (c.email || "").toLowerCase().includes(s) ||
      (c.name || "").toLowerCase().includes(s) ||
      (c.company || "").toLowerCase().includes(s)
    );
  });

  return (
    <Layout>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console · Clients</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Manage Client Accounts
        </h1>
        <p className="mt-3 text-zinc-600 max-w-2xl text-sm">
          When a client forgets their password, reset it here. Their existing sessions will be signed out, and you can
          copy the new credentials to send them.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6 md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="clients-search-input"
            placeholder="Search email, name, or company..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors"
          />
        </div>
        <button
          onClick={fetchClients}
          data-testid="clients-refresh-button"
          className="inline-flex items-center gap-2 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white px-4 py-3 font-medium transition-colors"
        >
          <ArrowClockwise size={16} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="clients-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Client</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Company</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Orders</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Added</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1", "s-2", "s-3"].map((k) => (
                <TableRow key={k}>
                  <TableCell colSpan={5}>
                    <div className="h-6 bg-zinc-100 animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-zinc-500">
                  {q ? "No clients match your search." : "No clients yet. Create one from the Orders page."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.user_id} className="hover:bg-zinc-50 border-zinc-200">
                  <TableCell>
                    <div className="font-medium text-zinc-900">{c.name}</div>
                    <div className="mt-tiny text-zinc-500 font-mono-mt">{c.email}</div>
                  </TableCell>
                  <TableCell>{c.company || <span className="text-zinc-400">—</span>}</TableCell>
                  <TableCell className="font-mono-mt">{c.order_count ?? 0}</TableCell>
                  <TableCell className="text-sm text-zinc-600">{formatDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => setTarget(c)}
                      data-testid={`reset-password-${c.email}`}
                      className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
                    >
                      <KeyReturn size={14} /> Reset password
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ResetPasswordDialog
        target={target}
        onClose={() => setTarget(null)}
        onDone={() => {
          setTarget(null);
          fetchClients();
        }}
      />
    </Layout>
  );
}

function ResetPasswordDialog({ target, onClose, onDone }) {
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (target) {
      setNewPassword(generatePassword());
      setResult(null);
    }
  }, [target]);

  const submit = async () => {
    if (!target) return;
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/clients/${target.user_id}/reset-password`, { new_password: newPassword });
      setResult(res.data);
      toast.success("Password reset");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const copyCredentials = () => {
    if (!result) return;
    const text = `MaterialTrack access\nURL: ${window.location.origin}\nEmail: ${result.email}\nPassword: ${result.new_password}`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied");
  };

  const close = () => {
    setResult(null);
    setNewPassword("");
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-none border-zinc-200 max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {result ? "New credentials ready" : "Reset client password"}
          </DialogTitle>
          <DialogDescription>
            {result ? (
              <>Copy these credentials and message them to <span className="font-mono-mt">{result.email}</span>.</>
            ) : target ? (
              <>Set a new password for <span className="font-mono-mt">{target.email}</span>. Their existing sessions will be signed out.</>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="border-l-2 border-[#0047AB] bg-blue-50/50 pl-4 py-3">
              <div className="mt-tiny text-zinc-500">New credentials</div>
              <div className="font-mono-mt text-sm mt-2 space-y-1">
                <div><span className="text-zinc-500">Email:</span> {result.email}</div>
                <div><span className="text-zinc-500">Password:</span> {result.new_password}</div>
              </div>
            </div>
            <button
              onClick={copyCredentials}
              data-testid="reset-copy-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2.5 font-medium transition-colors"
            >
              <Copy size={16} /> Copy full credentials
            </button>
            <button
              onClick={() => { onDone(); close(); }}
              className="w-full border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
              data-testid="reset-done-button"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-amber-50 border-l-2 border-amber-500 pl-3 py-2.5 text-sm text-amber-900">
              <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
              <div>
                Passwords are one-way hashed — we can&apos;t recover the client&apos;s original one.
                Setting a new password is the only option.
              </div>
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5 flex items-center justify-between">
                <span>New password</span>
                <button
                  type="button"
                  onClick={() => setNewPassword(generatePassword())}
                  className="text-[11px] text-[#0047AB] hover:underline"
                  data-testid="reset-regen-button"
                >
                  regenerate
                </button>
              </div>
              <Input
                data-testid="reset-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-none h-11 border-zinc-200 font-mono-mt"
              />
            </div>
          </div>
        )}

        {!result && (
          <DialogFooter>
            <button
              onClick={close}
              className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
              data-testid="reset-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="reset-submit-button"
              className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
            >
              {busy ? "Resetting..." : "Reset password"}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
